import { BUILD, pieceOf } from "@eldrun/content";
import { boxTopAt, buildRefusal, pieceBoxes, pieceCenter, segmentBoxEntry, snapPiece, type Box, type BuildWorld, type GridPos, type PieceInfo, type PieceKind } from "@eldrun/sim";

/** O que o cliente lê de cada estrutura do schema. */
export interface StructureLike {
  itemId: string;
  grid: boolean;
  gx: number;
  gy: number;
  gz: number;
  rot: number;
  open: boolean;
  owner: string;
  hp: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Peças da grade do schema, como o grafo de suporte as vê. */
export function piecesFrom(structures: { forEach(cb: (s: StructureLike, id: string) => void): void }): PieceInfo[] {
  const out: PieceInfo[] = [];
  structures.forEach((s, id) => {
    const spec = s.grid ? pieceOf(s.itemId) : undefined;
    if (spec) out.push({ id, kind: spec.kind as PieceKind, gx: s.gx, gy: s.gy, gz: s.gz, rot: s.rot, open: s.open });
  });
  return out;
}

/** Peça na mira (primeira caixa no raio de `max` m), com o ponto e a caixa atingidos. */
export function aimPiece(origin: Vec3, dir: Vec3, pieces: readonly PieceInfo[], max: number): { piece: PieceInfo; point: Vec3; box: Box } | undefined {
  const end = { x: origin.x + dir.x * max, y: origin.y + dir.y * max, z: origin.z + dir.z * max };
  let best: { piece: PieceInfo; t: number; box: Box } | undefined;
  for (const p of pieces) {
    for (const b of pieceBoxes(p.kind, p, p.open)) {
      const t = segmentBoxEntry(origin, end, b);
      if (t !== undefined && (!best || t < best.t)) best = { piece: p, t, box: b };
    }
  }
  if (!best) return undefined;
  const t = best.t;
  return { piece: best.piece, box: best.box, point: { x: origin.x + (end.x - origin.x) * t, y: origin.y + (end.y - origin.y) * t, z: origin.z + (end.z - origin.z) * t } };
}

export interface BuildPreview {
  pos: GridPos;
  center: { x: number; y: number; z: number; yaw: number };
  /** Motivo de não dar (o mesmo critério do servidor), ou `undefined` se dá. */
  blocker: string | undefined;
}

/**
 * Prévia da peça na mira (doc 03): encaixe na grade a partir do ponto (o topo da peça mirada vira
 * o nível; a lateral, o nível da base dela) e as mesmas regras que o servidor confere
 * (`buildRefusal`: alcance do olho do corpo, limite, ocupação, terreno, água, vista e apoio).
 * Corpos no caminho ficam só no servidor.
 */
export function buildPreview(kind: PieceKind, hit: Vec3, rot: number, eye: Vec3, pieces: readonly PieceInfo[], world: BuildWorld): BuildPreview {
  const pos = snapPiece(kind, hit, rot);
  return { pos, center: pieceCenter(kind, pos), blocker: buildRefusal(kind, pos, eye, pieces, world, BUILD) };
}

/** Ponto de encaixe ao mirar uma peça: o topo dela (para empilhar) ou o nível da base (de lado). */
export function hitLevel(point: Vec3, box: Box): Vec3 {
  const top = boxTopAt(box, point.x, point.z);
  return Math.abs(point.y - top) < 0.05 ? { ...point, y: top } : { ...point, y: box.minY };
}

/**
 * Peças que este jogador mandou desmontar (F90/F95), cada uma por `ttlMs`: a que sai do estado nesse
 * prazo foi o desmonte dele (se desfaz, sem entulho). Um desmonte recusado pelo servidor não deixa o id
 * para trás: uma quebra ou uma cascata depois mostraria a ele o dissolve e aos outros o entulho.
 */
export class PendingDismantles {
  private readonly at = new Map<string, number>();
  private readonly ttlMs: number;

  constructor(ttlMs = 1500) {
    this.ttlMs = ttlMs;
  }

  add(id: string, now: number): void {
    for (const [k, t] of this.at) if (now - t > this.ttlMs) this.at.delete(k);
    this.at.set(id, now);
  }

  /** A peça saiu agora: foi o desmonte dele (dentro do prazo)? Sai da lista de qualquer jeito. */
  take(id: string, now: number): boolean {
    const t = this.at.get(id);
    this.at.delete(id);
    return t !== undefined && now - t <= this.ttlMs;
  }

  get size(): number {
    return this.at.size;
  }
}
